import React, { Suspense, lazy } from 'react';
import {
	BrowserRouter as Router,
	Routes,
	Route,
	Outlet,
	Navigate,
} from 'react-router-dom';
import { SidebarProvider, useSidebar } from '../../context/LayoutContext';
import BetaSidebar from './BetaSidebar';
import BetaBackdrop from './BetaBackdrop';
import BetaHeader from './BetaHeader';
import Dashboard from './Dashboard';
import { ModalProvider } from '../../context/ModalContext';
import { SettingsProvider } from '../../context/SettingsContext';
import { AppProvider, useApp } from '../../context/AppContextBeta';
import Assets from '../Assets';
import SingleAsset from '../Assets/Single';
import useSettings from '../../hooks/useSettings';
import Settings, { CustomFieldsTable, SettingsEntry } from '../Settings';
import ProtectedRoute from '../../components/helpers/ProtectedRoute';
import AllList from '../Assets/AllList';
import AddAssetModal from '../../modals/Assets/AddAssetModal';
import AddAssetModalEntry from '../../modals/Assets/AddAssetModalEntry';
import GlobalSearch from '../../modals/GlobalSearch';
import SelectLocationModal from '../../modals/SelectLocationModal';
import UploadDocuments from '../../modals/UploadDocuments';
import AddAttributes from '../../modals/AddAttributes';
import ManageAttributes from '../../modals/ManageAttributes';
import QuickAddCategory from '../../modals/QuickAddCategory';
import QuickAddTag from '../../modals/QuickAddTag';
import DeleteModal from '../../modals/DeleteModal';
import AssetsNearbySlider from '../../modals/AssetsNearby';
import AddContactSlider from '../../modals/AddContactSlider';
import ViewContactModal from '../../modals/ViewContactModal';
import QuickAddPowerUsers from '../../modals/QuickAddPowerUsers';
import QuickAddVendor from '../../modals/QuickAddVendor';
import ContactModal from '../../modals/ContactModal';
import Tickets, { TicketsList } from '../Tickets';
import TicketsBeta from '../Tickets/TicketsBeta';
import WorkOrders from '../WorkOrders';
import Projects from '../Projects';
import ScheduleCalendar from '../ScheduleCalendar';
import TicketOverview from '../../modals/TicketOverview';
import TicketOverviewTablet from '../../modals/TicketOverviewTablet';
import TaskDetailModal from '../../modals/TaskDetailModal';
import SopViewerModal from '../../modals/SopViewerModal';
import CreateTaskModal from '../../modals/CreateTaskModal';
import EditRecurrenceRuleModal from '../../modals/EditRecurrenceRuleModal';
import RecurrenceRuleHistoryModal from '../../modals/RecurrenceRuleHistoryModal';
import CreateTicketModal from '../../modals/CreateTicketModal';
import EditTicketModal from '../../modals/EditTicketModal';
import StatisticsModal from '../../modals/StatisticsModal';
import WorkOrderOverview from '../../modals/WorkOrderOverview';
import CreateWorkOrderModal from '../../modals/CreateWorkOrderModal';
import CreateProjectModal from '../../modals/CreateProjectModal';
import ProjectOverviewModal from '../../modals/ProjectOverviewModal';
import EditSopModal from '../../modals/EditSopModal';
import PublishSopModal from '../../modals/PublishSopModal';
import EditKbArticleModal from '../../modals/EditKbArticleModal';
import DocumentPickerModal from '../../modals/DocumentPickerModal';
const DocumentPreviewModal = lazy(() => import('../../modals/DocumentPreviewModal'));
import TicketsListModal from '../../modals/TicketsListModal';
import WorkOrdersListModal from '../../modals/WorkOrdersListModal';
import EditPMScheduleModal from '../../modals/EditPMScheduleModal';
import EditWorkOrderModal from '../../modals/EditWorkOrderModal';
import ServiceReportUploadModal from '../../modals/ServiceReportUploadModal';
import PMScheduleOverview from '../../modals/PMScheduleOverview';
import PMSchedulePickerModal from '../../modals/PMSchedulePickerModal';
import SOPs from '../SOPs';
import KnowledgeBase from '../KnowledgeBase';
import PMSchedules from '../PMSchedules';
import { Contracts } from '../Contracts';
import AddContractBetaModal from '../../modals/Contracts/AddContractBetaModal';
const ContractOverview = lazy(() => import('../../modals/ContractOverview'));
import DeleteContractModal from '../../modals/DeleteContractModal';
import ContractPickerModal from '../../modals/ContractPickerModal';
import VendorPickerModal from '../../modals/VendorPickerModal';
import ContactsList from '../Directory/List';
import Profile from '../Directory/Profile';
import ManageContact from '../Directory/ManageContact';
import DocumentsEntry from '../Documents';
import Locations from '../Locations';
import AddLocation from '../../modals/AddLocation';
import Tasks, { TasksEntry } from '../Tasks';
import ServiceForm from '../Tasks/ServiceForm';
import AddTaskModal from '../../modals/Tasks/AddTaskModal';
import Team from '../Team';
import PowerUserProfile from '../Team/PowerUserProfile';
import Integrations from '../Settings/Integrations';
import Modules from '../Settings/Modules';
import Integration from '../Settings/Integrations/Integration';
import TabletView from '../../modals/TabletView';
import { SocketProvider } from '../../context/SocketContext';
import { Bookings } from '../Bookings';
import AddBookingModal from '../../modals/Bookings/AddBookingModal';
import BookingOverview from '../../modals/Bookings/BookingOverview';
import ArchivedAssets from '../Settings/ArchivedAssets';
import QRCodeSetting from '../Settings/QRCode';
import AssignmentSettings from '../Settings/Assignments';
import UserSettings from '../Settings/UserSettings';
import AccountSettings from '../Settings/Account';
import NotificationSettings from '../Settings/Notifications';
import ApiTokens from '../Settings/ApiTokens';
import AssetSettings from '../Settings/AssetSettings';
import ESignaturesSettings from '../Settings/ESignatures';
import AuditLog from '../AuditLog';
import Documents from '../Documents/Documents';

export const Layout = () => {
	const { isExpanded, isHovered, isMobileOpen } = useSidebar();
	const { theme } = useApp();
	return (
		<div className="min-h-screen xl:flex">
			<div>
				<BetaSidebar />
				<BetaBackdrop />
			</div>
			<div
				className={`flex-1 transition-all duration-300 ease-in-out ${
					isExpanded || isHovered ? 'lg:ml-[290px]' : 'lg:ml-[90px]'
				} ${isMobileOpen ? 'ml-0' : ''} min-w-0 overflow-x-auto`}
			>
				<BetaHeader />
				<Outlet />
			</div>
		</div>
	);
};

const BetaDashboard = () => {
	const {
		get: getSettings,
		set: updateSettings,
		loading: isLoadingSettings,
	} = useSettings();

	return (
		<>
			<AppProvider>
				<SidebarProvider>
					<ModalProvider>
						<SocketProvider>
							<Routes>
								{/* Dashboard Layout */}
								<Route element={<Layout />}>
									<Route
										index
										path="/"
										element={
											getSettings('userLandingPage')?.value === 'allAssets' ? (
												<Navigate replace to="/assets/all" />
											) : (
												<Navigate replace to="/dashboard" />
											)
										}
									/>
									<Route path="/dashboard" element={<Dashboard />} />
									<Route path="/bookings" element={<Outlet />}>
										<Route index element={<Bookings />} />
										<Route path={':id'} element={<Bookings />} />
									</Route>
									<Route path="/assets" element={<Assets />}>
										<Route index element={<Navigate to="all" replace />} />

										<Route path="all" element={<Outlet />}>
											<Route index element={<AllList />} />
											<Route path="issues" element={<AllList />} />
										</Route>

										<Route path="table/:tableId" element={<AllList />} />
										<Route path="tags/:tagId" element={<AllList />} />
										<Route path="location/:locationId" element={<AllList />} />

										<Route path=":id" element={<Outlet />}>
											<Route
												index
												element={
													<Navigate to="overview" replace relative="path" />
												}
											/>
											<Route path={':tab'} element={<SingleAsset />} />
										</Route>
										<Route path="*" element={<div>Not found</div>} />
									</Route>
									<Route path="/tickets" element={<Outlet />}>
										<Route index element={<TicketsList />} />
										<Route path={'create'} element={<TicketsList />} />
										<Route path={'status/:param'} element={<TicketsList />} />
										<Route path={':id'} element={<TicketsList />} />
									</Route>
									<Route path="/tickets-beta/:id?" element={<TicketsBeta />} />

									<Route path="/work-orders" element={<Outlet />}>
										<Route index element={<WorkOrders />} />
										<Route path={':id'} element={<WorkOrders />} />
									</Route>

									<Route path="/calendar" element={<ScheduleCalendar />} />

									<Route path="/sops" element={<SOPs />} />
									<Route path="/knowledge-base" element={<KnowledgeBase />} />

									<Route path="/pm-schedules" element={<Outlet />}>
										<Route index element={<PMSchedules />} />
										<Route path={':id'} element={<PMSchedules />} />
									</Route>

									<Route path="/audit-log" element={<AuditLog />} />

									<Route path="/projects" element={<Outlet />}>
										<Route
											index
											element={
												<ProtectedRoute role="default:superadmin">
													<Projects />
												</ProtectedRoute>
											}
										/>
										<Route
											path={':id'}
											element={
												<ProtectedRoute role="default:superadmin">
													<Projects />
												</ProtectedRoute>
											}
										/>
									</Route>

									<Route path="/manage/contracts" element={<Outlet />}>
										<Route index element={<Contracts />} />
										<Route path={':id'} element={<Contracts />} />
									</Route>

									<Route path="/manage/directory" element={<ContactsList />} />
									<Route path="/manage/directory/new" element={<ManageContact />} />
									<Route path="/manage/directory/:id" element={<Profile />} />
									<Route path="/manage/directory/:id/edit" element={<ManageContact />} />

									<Route path="/manage/documents" element={<Documents />} />
									<Route path="/manage/locations" element={<Locations />} />

									<Route path="/manage/tasks" element={<Outlet />}>
										<Route index element={<TasksEntry />} />

										<Route path={'new'} element={<ServiceForm />} />
										<Route path={':id'} element={<TasksEntry />} />
										<Route path={':id/edit'} element={<ServiceForm />} />
									</Route>

									<Route path="/manage/team" element={<Outlet />}>
										<Route index element={<Team />} />

										<Route path={'new'} element={<Team />} />
										<Route path={'power-user/:id'} element={<PowerUserProfile />} />
										<Route path={':tab'} element={<Team />} />
									</Route>

									<Route path="/settings" element={<SettingsEntry />}>
										<Route
											index
											element={<Navigate to="data-structures" replace />}
										/>

										<Route
											path="data-structures"
											element={<CustomFieldsTable />}
										/>
										<Route path="data-structures/:id" element={<Modules />} />
										<Route
											path="data-structures/:id/:tab"
											element={<Modules />}
										/>

										<Route path="integrations" element={<Integrations />} />
										<Route path="integrations/:id" element={<Integration />} />
										<Route
											path="integrations/:id/:tab"
											element={<Integration />}
										/>

										<Route path="archived" element={<ArchivedAssets />} />
										<Route path="asset-tags" element={<QRCodeSetting />} />
										<Route
											path="assignments"
											element={<AssignmentSettings />}
										/>
										<Route path="ux-preferences" element={<UserSettings />} />
										<Route path="profile" element={<AccountSettings />} />
										<Route
											path="notifications"
											element={<NotificationSettings />}
										/>
										<Route path="api-tokens" element={<ApiTokens />} />
										<Route path="assets" element={<AssetSettings />} />
										<Route
											path="e-signatures"
											element={<ESignaturesSettings />}
										/>
									</Route>

									{/** Assets Routing 
						

						<Route index path="/settings" element={<Settings />} />
						*/}
								</Route>

								{/* Fallback Route */}
								<Route path="*" element={<div>Not found</div>} />
							</Routes>

							<DeleteModal />

							<AddAttributes />
							<ManageAttributes />
							<QuickAddCategory />
							<QuickAddTag />

							<AddAssetModal />
							<AddAssetModalEntry />

							<GlobalSearch />
							<SelectLocationModal />
							<SelectLocationModal modalName="selectParentLocation" />

							<UploadDocuments />

							<AssetsNearbySlider />
							<QuickAddPowerUsers />
							<ViewContactModal />
							<ContactModal />
							<QuickAddVendor />

							<TicketOverview />
							<TicketOverviewTablet />
							<TaskDetailModal />
							<SopViewerModal />
							<CreateTaskModal />
							<EditRecurrenceRuleModal />
							<RecurrenceRuleHistoryModal />
							<CreateTicketModal />
							<EditTicketModal />

							<WorkOrderOverview />
							<StatisticsModal />
							<CreateWorkOrderModal />
							<EditWorkOrderModal />
							<ServiceReportUploadModal />

							<EditSopModal />
							<PublishSopModal />
							<EditKbArticleModal />
							<DocumentPickerModal />
							<Suspense fallback={null}><DocumentPreviewModal /></Suspense>
							<TicketsListModal />
							<WorkOrdersListModal />
							<EditPMScheduleModal />
							<PMScheduleOverview />
							<PMSchedulePickerModal />

							<AddContractBetaModal />
							<Suspense fallback={null}><ContractOverview /></Suspense>
							<DeleteContractModal />
							<ContractPickerModal />
							<VendorPickerModal />

							<AddLocation />
							<AddTaskModal />
							<TabletView />

							<AddBookingModal />
							<BookingOverview />

							<CreateProjectModal />
							<ProjectOverviewModal />
						</SocketProvider>
					</ModalProvider>
				</SidebarProvider>
			</AppProvider>
		</>
	);
};

export default BetaDashboard;
